import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIClient } from "../../helpers/api_helper";
import * as url from "../../helpers/url_helper";

const api = new APIClient();

/** Fetch all website templates (public) */
export const fetchWebsiteTemplates = createAsyncThunk(
  "websiteBuilder/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(url.GET_WEBSITE_TEMPLATES);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Check slug availability */
export const checkSlugAvailability = createAsyncThunk(
  "websiteBuilder/checkSlug",
  async (slug, { rejectWithValue }) => {
    try {
      const response = await api.get(url.CHECK_WEBSITE_SLUG, { params: { slug } });
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Create a new website draft */
export const createWebsite = createAsyncThunk(
  "websiteBuilder/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.create(url.CREATE_WEBSITE, data);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Quick publish: create + AI generate + deploy in one shot (after payment) */
export const quickPublishWebsite = createAsyncThunk(
  "websiteBuilder/quickPublish",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.create(url.QUICK_PUBLISH_WEBSITE, data);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Generate AI content */
export const generateWebsiteContent = createAsyncThunk(
  "websiteBuilder/generateContent",
  async (websiteId, { rejectWithValue }) => {
    try {
      const response = await api.create(`${url.GENERATE_WEBSITE_CONTENT}/${websiteId}/generate`);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Update website content */
export const updateWebsiteContent = createAsyncThunk(
  "websiteBuilder/updateContent",
  async ({ websiteId, content }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${url.UPDATE_WEBSITE_CONTENT}/${websiteId}/content`, content);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Update website template */
export const updateWebsiteTemplate = createAsyncThunk(
  "websiteBuilder/updateTemplate",
  async ({ websiteId, templateId }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${url.UPDATE_WEBSITE_TEMPLATE}/${websiteId}/template`, { templateId });
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Edit website: change template, regenerate AI content & redeploy */
export const editWebsite = createAsyncThunk(
  "websiteBuilder/edit",
  async ({ websiteId, templateId }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${url.EDIT_WEBSITE}/${websiteId}/edit`, { templateId });
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Mark payment complete */
export const markWebsitePaymentComplete = createAsyncThunk(
  "websiteBuilder/paymentComplete",
  async ({ websiteId, razorpayOrderId, razorpayPaymentId }, { rejectWithValue }) => {
    try {
      const response = await api.create(`${url.WEBSITE_PAYMENT_COMPLETE}/${websiteId}/payment-complete`, {
        razorpayOrderId,
        razorpayPaymentId,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Publish website */
export const publishWebsite = createAsyncThunk(
  "websiteBuilder/publish",
  async (websiteId, { rejectWithValue }) => {
    try {
      const response = await api.create(`${url.PUBLISH_WEBSITE}/${websiteId}/publish`);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Get all user websites */
export const fetchUserWebsites = createAsyncThunk(
  "websiteBuilder/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(url.GET_USER_WEBSITES);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Get a single website */
export const fetchWebsite = createAsyncThunk(
  "websiteBuilder/fetchOne",
  async (websiteId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${url.GET_WEBSITE}/${websiteId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Get website leads */
export const fetchWebsiteLeads = createAsyncThunk(
  "websiteBuilder/fetchLeads",
  async (websiteId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${url.GET_WEBSITE_LEADS}/${websiteId}/leads`);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/** Delete a website */
export const deleteWebsite = createAsyncThunk(
  "websiteBuilder/delete",
  async (websiteId, { rejectWithValue }) => {
    try {
      await api.delete(`${url.DELETE_WEBSITE}/${websiteId}`);
      return websiteId;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);
